import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ModalNavigationEnum } from '../constants';

export interface TabsNavigateParams {
    modal: ModalNavigationEnum;
    param: string;
    value?: any;
    extras?: NavigationExtras;
}

export interface TabsNavigateMultiParams {
    modal: ModalNavigationEnum;
    paramValues: Record<string, any>;
    extras?: NavigationExtras;
}

@Injectable()
export class TabsNavigationService implements OnDestroy {
    private readonly registeredParams: Set<string> = new Set();

    constructor(
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
    ) {}

    getParamValue<T>(param: string): Observable<T> {
        return this.activatedRoute.queryParams.pipe(
            map((params: Params) => params?.[param]),
        );
    }

    listenToQueryParamValueChange<T>(
        param: string,
        initialNavigateParams?: TabsNavigateParams
    ): Observable<T> {
        const queryParams: Observable<T> = this.activatedRoute.queryParams.pipe(
            map((params: Params) => params?.[param])
        );

        if (initialNavigateParams) {
            queryParams.pipe(take(1)).subscribe((param: T) => {
                if (!param) {
                    this.changeParamValue({
                        ...initialNavigateParams,
                        extras: {
                            replaceUrl: true
                        }
                    });
                }
            })
        }
        this.registeredParams.add(param);

        return queryParams;
    }

    changeParamValue({ modal, value, param, extras = {} }: TabsNavigateParams): void {
        this.router.navigate(['/', { outlets: { modal: [modal] } }], {
            ...extras,
            queryParams: { [param]: value },
            queryParamsHandling: 'merge',
        });
    }

    changeMultipleParamValues({ modal, paramValues, extras = {} }: TabsNavigateMultiParams): void {
        Object.keys(paramValues).forEach(key => this.registeredParams.add(key));

        this.router.navigate(['/', { outlets: { modal: [modal] } }], {
            ...extras,
            queryParams: paramValues,
            queryParamsHandling: 'merge',
        });
    }

    ngOnDestroy(): void {
        this.router.navigate([], {
            queryParams: Array.from(this.registeredParams).reduce(
                (acc: Params, paramKey: string) => ({
                    ...acc,
                    [paramKey]: null,
                }),
                {}
            ),
            queryParamsHandling: 'merge',
        });
        this.registeredParams.clear();
    }
}
